import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './productDetails.css';
import { useCart } from '../contexts/CartContext';
import AllReviews from './AllReviews';
import LimitedStockBanner from './LimitedStockBanner';
import RelatedProducts from './RelatedProducts';
import { FaShareAlt, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { useSwipeable } from 'react-swipeable';

const ProductDetails = () => {
  const { productId, color } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [selectedColorImages, setSelectedColorImages] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [activePanel, setActivePanel] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const shareLink = process.env.REACT_APP_SHARE_LINK;


  const handleNextImage = () => {
    const newIndex =
      currentImageIndex === selectedColorImages.length - 1
        ? 0
        : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
    setMainImage(`${baseUrl}/${selectedColorImages[newIndex]}`);
  };

  const handlePrevImage = () => {
    const newIndex =
      currentImageIndex === 0
        ? selectedColorImages.length - 1
        : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
    setMainImage(`${baseUrl}/${selectedColorImages[newIndex]}`);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleNextImage(),
    onSwipedRight: () => handlePrevImage(),
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await axios.get(`${baseUrl}/product/${productId}/${color}`);
        setProduct(response.data);
        setSelectedColorImages(response.data.selectedColorImages);
        setMainImage(`${baseUrl}/${response.data.selectedColorImages[0]}`);
        setSelectedColor(response.data.selectedColor);
      } catch (error) {
        console.error('Error fetching product details:', error);
        navigate('/');
      }
    };

    fetchProductDetails();
  }, [productId, color, navigate, baseUrl]);

  if (!product) {
    return <div>Loading...</div>;
  }

  const {
    title,
    name,
    shortDescription,
    listingPrice,
    sellingPrice,
    details,
    longDescription,
    about,
    allColors,
    categoryId,
  } = product;

  const handleAddToCart = () => {
    const productData = {
      image: mainImage,
      title: name,
      description: shortDescription,
      listingPrice,
      sellingPrice,
      details: longDescription,
      about,
      images: selectedColorImages,
      color: selectedColor,
      productId,
    };
    addToCart(productData);
  };

  const handleBuyNow = () => {
    const productData = {
      image: mainImage,
      title: name,
      description: shortDescription,
      listingPrice,
      sellingPrice,
      details: longDescription,
      about,
      images: selectedColorImages,
      color: selectedColor,
      productId,
    };

    if (localStorage.getItem('token')) {
      addToCart(productData);
      navigate('/checkout');
    } else {
      localStorage.setItem('pendingProduct', JSON.stringify(productData));
      addToCart(productData);
      navigate('/register');
    }

    localStorage.setItem('BuyNowClicked', 'true');
  };

  const handleColorChange = (colorImages, colorName) => {
    setSelectedColorImages(colorImages);
    setSelectedColor(colorName);
    setMainImage(`${baseUrl}/${colorImages[0]}`);
    setVisibleStartIndex(0);
  };

  const percentageDifference = Math.round(((listingPrice - sellingPrice) / listingPrice) * 100);

  const shareProduct = () => {
    const shareUrl = `${shareLink}/product/${productId}/${selectedColor}`;
    if (navigator.share) {
      navigator
        .share({
          title: name,
          url: shareUrl,
        })
        .catch(console.error);
    } else {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(name)}`,
        '_blank'
      );
    }
  };

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  return (
    <div className="product-details-page">
      <div className="product-details-container">
        <div className="image-gallery">
          <div className="thumbnail-container">
            {visibleStartIndex > 0 && (
              <div className="arrow-toggle" onClick={() => setVisibleStartIndex(visibleStartIndex - 1)}>
                <FaChevronUp />
              </div>
            )}

            {selectedColorImages
              .slice(visibleStartIndex, visibleStartIndex + 5)
              .map((img, index) => (
                <img
                  key={visibleStartIndex + index}
                  src={`${baseUrl}/${img}`}
                  alt={`${name} ${visibleStartIndex + index + 1}`}
                  className="thumbnail"
                  onMouseOver={() => setMainImage(`${baseUrl}/${img}`)}
                />
              ))}

            {visibleStartIndex + 4 < selectedColorImages.length && (
              <div className="arrow-toggle" onClick={() => setVisibleStartIndex(visibleStartIndex + 1)}>
                <FaChevronDown />
              </div>
            )}
          </div>

          <div className="main-image-container" {...handlers}>
            <img src={mainImage} alt={name} className="main-image" />
          </div>
        </div>

        <div className="image-indicators">
          {selectedColorImages.map((_, index) => (
            <span
              key={index}
              className={`indicator-dot ${index === currentImageIndex ? 'active-dot' : ''}`}
            ></span>
          ))}
        </div>

        <div className="product-info">
          <div className='product-name'>
            <h1>{name}</h1>
          </div>
          <p className="product-description">{shortDescription}</p>
          <p className="price-line">
            <label className='MRP'>MRP:</label> &nbsp;
            <span className="product-price"><s>₹ {listingPrice}</s></span> &nbsp;
            <span className="product-selling-price">₹ {sellingPrice}</span>
          </p>
          <span className='taxes'>(Inclusive Of All Taxes.)</span>
          <span className='cod'>✅ COD Available.</span>

          <p className="percentage-difference">Save {percentageDifference}%</p>
          <div className="product-info"><LimitedStockBanner /></div>
          <p className="product-id"><strong>Product ID: </strong>{productId}</p>
          <div className="purchase-buttons">
            <button className="add-to-cart-btn" onClick={handleAddToCart}>Add to Cart</button>
            <button className="buy-now-btn" onClick={handleBuyNow}>Buy Now</button>
            <FaShareAlt className="share-icon" onClick={shareProduct} />
          </div>

          <div className="other-colors">
            <p className="product-color"><strong>Selected Color: </strong>{selectedColor}</p>
            <div className="colors-container">
              {allColors.map((colorItem, index) => (
                <div key={index} className="color-item" onClick={() => handleColorChange(colorItem.images, colorItem.color)}>
                  <p>{colorItem.color}</p>
                  <img
                    src={`${baseUrl}/${colorItem.images[0]}`}
                    alt={`${name} ${colorItem.color}`}
                    className="color-thumbnail"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bottom-section-container">
        <div className="badge-box">
          <p className="highlight">You are currently viewing: {name} | {selectedColor}</p>
          <p>🏆 No 1 Backpack Brand on AMAZON India</p>
          <p>🚚 Delivered In 3-7 Days Across India</p>
          <p>⭐ Over 1 Million Happy Customers</p>
          <p>💵 ✅ COD & Free Shipping On All Orders</p>
        </div>

        <div className="description-container">
          {/* DESCRIPTION */}
          <div className="description-toggle-container">
            <button
              className="description-toggle-header"
              onClick={() => setActivePanel(activePanel === 'description' ? null : 'description')}
            >
              <span className="description-label">Description</span>
              <span className="toggle-icon">{activePanel === 'description' ? '−' : '+'}</span>
            </button>
            {activePanel === 'description' && (
              <div className="description-toggle-content">
                <p><strong>Short Description:</strong> {shortDescription}</p>
                <p style={{ whiteSpace: 'pre-line' }}>
                  <strong>Detailed Description:</strong><br />
                  {longDescription}
                </p>
              </div>
            )}
          </div>

          {/* SHIPPING & DELIVERY */}
          <div className="Policy-toggle-container">
            <button
              className="Policy-toggle-header"
              onClick={() => setActivePanel(activePanel === 'shipping' ? null : 'shipping')}
            >
              <span className="Policy-label">Shipping & Delivery</span>
              <span className="toggle-icon">{activePanel === 'shipping' ? '−' : '+'}</span>
            </button>
            {activePanel === 'shipping' && (
              <div className="Policy-toggle-content">
                <p><strong>Shipping Delivery:</strong> Delivery in <strong>3–7 business days</strong> including handling and transit. Orders are processed quickly and shipped Monday to Saturday. We offer <strong>Free Shipping</strong> on all orders. Delivery times may vary during <strong>Public Holidays</strong>. For any questions, please contact customer support. Thank you for choosing <strong>Western Flex Shop!</strong></p>
              </div>
            )}
          </div>

          {/* RETURN POLICY */}
          <div className="Policy-toggle-container">
            <button
              className="Policy-toggle-header"
              onClick={() => setActivePanel(activePanel === 'return' ? null : 'return')}
            >
              <span className="Policy-label">Return Policy</span>
              <span className="toggle-icon">{activePanel === 'return' ? '−' : '+'}</span>
            </button>
            {activePanel === 'return' && (
              <div className="Policy-toggle-content">
                <p><strong>Return Policy:</strong> You can return products within 5 days if they are defective, the wrong color, or the wrong item. Products must be in original condition with all accessories and tags. Return shipping is covered by us for these cases. Please record an unboxing video as proof to support any return or damage claims.</p>
              </div>
            )}
          </div>

          {/* CUSTOMER SUPPORT */}
          <div className="Policy-toggle-container">
            <button
              className="Policy-toggle-header"
              onClick={() => setActivePanel(activePanel === 'support' ? null : 'support')}
            >
              <span className="Policy-label">Customer Support</span>
              <span className="toggle-icon">{activePanel === 'support' ? '−' : '+'}</span>
            </button>
            {activePanel === 'support' && (
              <div className="Policy-toggle-content">
                <p><strong>Customer Support:</strong> For any questions, our customer support team usually replies quickly, but in some cases, responses may take 48–72 hours during high volumes. To contact us, please fill out this form:{" "}
                  <a href="https://westernflexshop.com/contact-us" target="_blank" rel="noopener noreferrer">Contact Support Form</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AllReviews productId={productId} />
      <RelatedProducts categoryId={categoryId} currentProductId={productId} />
    </div>
  );
};

export default ProductDetails;
